import { X } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';

import { NOTE_FEEDBACK_COMMENT_MAX_LENGTH } from '@nesso/session-contracts/session-note.commands';
import { Button } from '@nesso/ui/primitives/button';
import { CheckboxField } from '@nesso/ui/primitives/checkbox-field';
import { PopoverContent } from '@nesso/ui/primitives/popover';
import { TextareaField } from '@nesso/ui/primitives/textarea-field';

interface FeedbackOption {
    id: string;
    label: string;
}

// TODO: derive these options from noteFeedbackReasonLabels in @nesso/session-contracts
const feedbackOptions: FeedbackOption[] = [
    { id: 'inaccurate', label: 'Inaccurate information' },
    { id: 'irrelevant', label: 'Irrelevant information' },
    { id: 'missing', label: 'Missing key information' },
    { id: 'other', label: 'Other' },
];

interface FeedbackFormData {
    reasons: string[];
    feedback: string;
}

interface Props {
    onSubmit?: (data: FeedbackFormData) => void;
    onSkip?: () => void;
    onClose?: () => void;
}

export function NoteFeedbackPopover({ onSubmit, onSkip, onClose }: Props) {
    const { control, handleSubmit, reset } = useForm<FeedbackFormData>({
        defaultValues: {
            reasons: [],
            feedback: '',
        },
    });

    const handleFormSubmit = (data: FeedbackFormData) => {
        onSubmit?.(data);
        reset();
    };

    const handleSkip = () => {
        reset();
        onSkip?.();
    };

    const handleClose = () => {
        reset();
        onClose?.();
    };

    return (
        <PopoverContent className="min-w-[320px] max-w-[400px] p-6" side="top" align="end">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-semibold">What went wrong?</h4>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-70 hover:opacity-100"
                    onClick={handleClose}
                >
                    <X className="h-4 w-4" />
                </Button>
            </div>

            <form onSubmit={handleSubmit(handleFormSubmit)}>
                <Controller
                    name="reasons"
                    control={control}
                    render={({ field }) => (
                        <div className="flex flex-col gap-2 mb-3">
                            {feedbackOptions.map((option) => (
                                <CheckboxField
                                    key={option.id}
                                    id={option.id}
                                    label={option.label}
                                    checked={field.value.includes(option.id)}
                                    onCheckedChange={(checked) => {
                                        const newValue = checked
                                            ? [...field.value, option.id]
                                            : field.value.filter((id) => id !== option.id);
                                        field.onChange(newValue);
                                    }}
                                />
                            ))}
                        </div>
                    )}
                />

                <Controller
                    name="feedback"
                    control={control}
                    render={({ field, fieldState }) => (
                        <TextareaField
                            {...field}
                            id={field.name}
                            label="Additional feedback"
                            maxLength={NOTE_FEEDBACK_COMMENT_MAX_LENGTH}
                            className="min-h-[5rem] resize-none"
                            fieldProps={{ className: 'mb-3' }}
                            error={fieldState.error?.message ?? fieldState.invalid}
                        />
                    )}
                />

                <div className="flex gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={handleSkip}
                    >
                        Skip
                    </Button>
                    <Button type="submit" size="sm" className="flex-1">
                        Submit
                    </Button>
                </div>
            </form>
        </PopoverContent>
    );
}
